import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Container,
  Fade,
  Chip
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PageTitle from '../../../components/PageTitle';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import s3Utils from "../../../utils/s3Utils";

// Import sub-components
import SelfieSection from './Form217/SelfieSection';
import AssessmentSummary from './Form217/AssessmentSummary';
import AssessmentSummaryNew from './Form217/AssessmentSummaryNew';
import AssessmentSummaryNew2 from './Form217/AssessmentSummaryNew2';
import AssessorSummaryNC from './Form217/AssessorSummaryNC';
import TestingWitnessed from './Form217/TestingWitnessed';
import AttendanceSheet from './Form217/AttendanceSheet';
import ScopeAccreditation from './Form217/ScopeAccreditation';
import DeclarationImpartiality from './Form217/DeclarationImpartiality';
import { Print } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_DOT_NET_CORE_API_URL;


const Form217Report = () => {
  const location = useLocation();
  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  // Function to get assessmentId from URL query parameters with decryption
  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        console.log(
          "📍 Using decrypted assessmentId from URL query:",
          decryptedAssessmentId
        );
        return decryptedAssessmentId;
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return null;
  };

  // Function to get assessorId from URL query parameters with decryption
  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        const decryptedAssessorId = decryptText(encryptedAssessorId);
        console.log(
          "📍 Using decrypted assessorId from URL query:",
          decryptedAssessorId
        );
        return decryptedAssessorId;
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  const getAppId = () => {
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      return queryAppId;
    }
    return null;
  };

  const getAssessmentId = () => {
    const queryAssessmentId = getAssessmentIdFromQuery();
    if (queryAssessmentId) {
      return queryAssessmentId;
    }
    return null;
  };

  const getAssessorId = () => {
    const queryAssessorId = getAssessorIdFromQuery();
    if (queryAssessorId) {
      return queryAssessorId;
    }
    return null;
  };

  const [appId, setAppId] = useState(getAppId());
  const [assessmentId, setAssessmentId] = useState(getAssessmentId());
  const [assessorId, setAssessorId] = useState(getAssessorId());
  const [loading, setLoading] = useState(true); // Set to true to load data on mount
  const [reportData, setReportData] = useState(null); // Initialize with null to trigger loading
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [isDisplayNew01052026, setIsDisplayNew01052026] = useState(false);
  const [isDisplayNew15052026, setIsDisplayNew15052026] = useState(false);

  
  // Fetch report data from Form217report API
  useEffect(() => {
    if (assessmentId && appId) {
      loadReportData();
    }
  }, [assessmentId, appId]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/Form215report`, {
        params: {
          assessmentId: assessmentId,
          appId: appId
        }
      });

      const apiData = response.data;
      
      if (apiData.isstatus === false) {
        console.warn('API returned error:', apiData.message);
      }

      // Transform API response to match component structure
      const selfiesWithUrls = apiData.selfi ? await Promise.all(
        apiData.selfi.map(async (item) => ({
          LaboratoryName: item.laboratoryName || '',
          Name: item.name || '',
          Capacity: item.capacity || '',
          DateFrom: item.dateFrom || '',
          StartDate: item.startDate || '',
          StartSelfie: item.startSelfie ? await s3Utils.generateSignedUrlForFilename(item.startSelfie) : '',
          StartLat: item.startLatitude || '',
          StartLong: item.startLongitude || '',
          StopDate: item.stopDate || '',
          StopSelfie: item.stopSelfie ? await s3Utils.generateSignedUrlForFilename(item.stopSelfie) : '',
          StopLat: item.stopLatitude || '',
          StopLong: item.stopLongitude || ''
        }))
      ) : [];

      const transformedData = {
        selfies: selfiesWithUrls,
        
        naf6: {
          lblNAF6LabName: 'Laboratory assessed',
          lblNaf6Lab: apiData.laboratoryName || '',
          lblNaf6QM: apiData.qm || '',
          lblNAF6DateOfVisit: 'Date(s) of Visit',
          lblNaf6Date: apiData.assessmentDate || '',
          lblsubmissionDate: apiData.dateOfSubmission || '',
          lblNAF6TypeOfVisit: 'Type of Visit',
          lblNaf6AssessmenttYPE: apiData.assessmentType || '',
          lblNaf6field: apiData.labType || '',
          lblNaf6Discipline: apiData.disciplines || '',
          lblNaf6Fcility: apiData.facility || '',
          lblNaf6LA: apiData.laName || '',
          rptrNaf6: apiData.naf6?.map(item => ({
            Capacity: item.capacity || '',
            Name: item.name || ''
          })) || [],
          lblNaf6PrevVisitDate: apiData.previousVisitDate || '',
          lblNonConformities: apiData.nonConformities || '',
          lblNaf6AssmtSmry: apiData.assessmentSummary || '',
          lblAssmntTeam: apiData.assessmentTeamComment || '',
          lblNaf6NABL133: apiData.nabL133 || '',
          lblNaf6Nabl142: apiData.nabL142 || '',
          lblNaf6Nabl163: apiData.nabL163 || '',
          lblNaf6Nabl143: apiData.nabL143 || '',
          linbl143: apiData.showNABL143 ? true : false,
          CalibNote: apiData.showCalibrationNote ? true : false,
          lblNonConformty: 'Non-Conformities raised during the assessment',
          lblRecom: '',
          lblNaf6Recommend: apiData.recommendationOfAssessmentTeam || '',
          major: apiData.major || '0',
          minor: apiData.minor || '0',
          majorC4: apiData.majorC4 || '0',
          majorC5: apiData.majorC5 || '0',
          majorC6: apiData.majorC6 || '0',
          majorC7: apiData.majorC7 || '0',
          majorC8: apiData.majorC8 || '0',
          minorC4: apiData.minorC4 || '0',
          minorC5: apiData.minorC5 || '0',
          minorC6: apiData.minorC6 || '0',
          minorC7: apiData.minorC7 || '0',
          minorC8: apiData.minorC8 || '0'
        },
        
        naf4: apiData.rptr || [],
        naf3a: apiData.naf3a || [],
        
        naf1a: {
          cabName: apiData.laboratoryName || '',
          openingDate: apiData.openingDate || '',
          openingTime: apiData.openingTime || '',
          closingDate: apiData.closingDate || '',
          closingTime: apiData.closingTime || '',
          attendanceList: apiData.naf1AMeeting?.map(item => ({
            meeting: item.meeting || '',
            labpersonal: item.labPersonal || '',
            designation: item.designation || ''
          })) || []
        },
        
        form72: apiData.scope?.map(item => ({
          AssessorName: item.assessorName || '',
          LabLocationName: item.labLocationName || '',
          disciplineName: item.disciplineName || '',
          DisciplineId: item.disciplineId || '',
          GroupId: item.groupId || '',
          groupName: item.groupName || '',
          SubGroupId: item.subGroupId || '',
          subGrpName: item.subGroupName || '',
          GroupTested: item.groupTested || '',
          TypeOfTest: item.typeOfTest || '',
          scopeyear: item.scopeYear || '',
          unit: item.unit || '',
          measr: item.measurementUncertainty || item.measurementUncertaintyDisplay || '',
          status: item.status || '',
          AssessmentTechniqueUsed: item.assessmentTechniqueUsed || item.assessmentTechniqueDisplay || '',
          Remarks: item.remarks || ''
        })) || [],
        
        form74: apiData.dec?.map(item => ({
          Name: item.name || '',
          isExEmployee: item.isExEmployee || '0',
          isCabRelation: item.isCabRelation || '0'
        })) || []
      };
      setReportData(transformedData);
      if(apiData.dateto) {
      const assessmentEndDate = parseUSDate(apiData.dateto);
      const targetDate = new Date('2026-05-01');
      assessmentEndDate.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);
      const targetDate15052026 = new Date('2026-05-15');
      targetDate15052026.setHours(0, 0, 0, 0);
      const isDispalyNew01052026 = assessmentEndDate >= targetDate && assessmentEndDate < targetDate15052026;
      setIsDisplayNew01052026(isDispalyNew01052026);
      const isDispalyNew15052026 = assessmentEndDate >= targetDate15052026;
      setIsDisplayNew15052026(isDispalyNew15052026);
      }
    } catch (error) {
      console.error('Error loading report data:', error);
      showSnackbar('Error loading report data: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const parseUSDate = (dateStr) => {
  const [datePart] = dateStr.split(" ");
  const [month, day, year] = datePart.split("/").map(Number);
  return new Date(year, month - 1, day);
};

  const handleDownloadPDF = async () => {
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPos = 15;

      // Helper function to add new page if needed
      const checkPageBreak = (requiredSpace = 20) => {
        if (yPos + requiredSpace > pageHeight - 20) {
          doc.addPage();
          yPos = 15;
          return true;
        }
        return false;
      };

      // Helper function to add section header
      const addSectionHeader = (title) => {
        checkPageBreak(15);
        doc.setFillColor(102, 126, 234);
        doc.rect(10, yPos, pageWidth - 20, 10, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(title, pageWidth / 2, yPos + 7, { align: 'center' });
        doc.setTextColor(0, 0, 0);
        yPos += 15;
      };

      // Header
      doc.setFillColor(102, 126, 234);
      doc.rect(0, 0, pageWidth, 25, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Form 217 - Assessment Report', pageWidth / 2, 12, { align: 'center' });
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('National Accreditation Board for Testing and Calibration Laboratories', pageWidth / 2, 19, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      yPos = 30;

      // Report Metadata
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Lab Type: ${reportData.naf6?.lblNaf6field || 'N/A'}`, 10, yPos);
      yPos += 10;

      // 1. SELFIE SECTION - Assessor Selfie & Location Verification
      if (reportData.selfies && reportData.selfies.length > 0) {
        addSectionHeader('Assessor Selfie & Location Verification');
        
        reportData.selfies.forEach((selfie, index) => {
          checkPageBreak(80);
          
          // Assessment Details Header
          doc.setFillColor(243, 246, 249);
          doc.rect(10, yPos, pageWidth - 20, 8, 'F');
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          doc.text('Assessment Details', 12, yPos + 5.5);
          yPos += 8;
          
          // Basic Info Table
          autoTable(doc, {
            startY: yPos,
            body: [
              [
                { content: 'Laboratory', styles: { fontStyle: 'bold' } },
                { content: 'Assessor', styles: { fontStyle: 'bold' } },
                { content: 'Date(s) of Visit', styles: { fontStyle: 'bold' } }
              ],
              [
                selfie.LaboratoryName || '',
                `${selfie.Name}${selfie.Capacity ? ` (${selfie.Capacity})` : ''}`,
                selfie.DateFrom || ''
              ]
            ],
            styles: { fontSize: 8, cellPadding: 3 },
            theme: 'grid',
            margin: { left: 10, right: 10 },
          });
          
          yPos = doc.lastAutoTable.finalY + 5;
          checkPageBreak(40);
          
          // Start Details Header
          doc.setFillColor(243, 246, 249);
          doc.rect(10, yPos, pageWidth - 20, 8, 'F');
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.text('Start Details', 12, yPos + 5.5);
          yPos += 8;
          
          // Start Details Table
          autoTable(doc, {
            startY: yPos,
            head: [['Start Date & Time', 'Start Location', 'Start Selfie']],
            body: [
              [
                selfie.StartDate || 'N/A',
                `Lat: ${selfie.StartLat || 'N/A'}\nLong: ${selfie.StartLong || 'N/A'}`,
                selfie.StartSelfie ? selfie.StartSelfie : 'N/A'
              ]
            ],
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [102, 126, 234], textColor: 255, fontStyle: 'bold' },
            theme: 'grid',
            margin: { left: 10, right: 10 },
          });
          
          yPos = doc.lastAutoTable.finalY + 5;
          checkPageBreak(40);
          
          // Stop Details Header
          doc.setFillColor(243, 246, 249);
          doc.rect(10, yPos, pageWidth - 20, 8, 'F');
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.text('Stop Details', 12, yPos + 5.5);
          yPos += 8;
          
          // Stop Details Table
          autoTable(doc, {
            startY: yPos,
            head: [['Stop Date & Time', 'Stop Location', 'Stop Selfie']],
            body: [
              [
                selfie.StopDate || 'N/A',
                `Lat: ${selfie.StopLat || 'N/A'}\nLong: ${selfie.StopLong || 'N/A'}`,
                selfie.StopSelfie ? selfie.StopSelfie : 'N/A'
              ]
            ],
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [102, 126, 234], textColor: 255, fontStyle: 'bold' },
            theme: 'grid',
            margin: { left: 10, right: 10 },
          });
          
          yPos = doc.lastAutoTable.finalY + 10;
          
          // Add separator if there are more assessors
          if (index < reportData.selfies.length - 1) {
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.5);
            doc.line(10, yPos, pageWidth - 10, yPos);
            yPos += 10;
          }
        });
      }

      // 2. ASSESSMENT SUMMARY (NAF-6)
      if (reportData.naf6) {
        addSectionHeader('NAF-6: Summary of Assessment');
        
        const naf6Data = reportData.naf6;
        
        autoTable(doc, {
          startY: yPos,
          head: [['Field', 'Details']],
          body: [
            ['Laboratory Assessed', naf6Data.lblNaf6Lab || ''],
            ['Quality Manager', naf6Data.lblNaf6QM || ''],
            ['Date(s) of Visit', naf6Data.lblNaf6Date || ''],
            ['Submission Date', naf6Data.lblsubmissionDate || ''],
            ['Type of Visit', naf6Data.lblNaf6AssessmenttYPE || ''],
            ['Field', naf6Data.lblNaf6field || ''],
            ['Discipline', naf6Data.lblNaf6Discipline || ''],
            ['Facility', naf6Data.lblNaf6Fcility || ''],
            ['Lead Assessor', naf6Data.lblNaf6LA || ''],
          ],
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [102, 126, 234], textColor: 255, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [245, 247, 250] },
          margin: { left: 10, right: 10 },
        });
        
        yPos = doc.lastAutoTable.finalY + 10;
        checkPageBreak(20);
        
        // Assessment Team
        if (naf6Data.rptrNaf6 && naf6Data.rptrNaf6.length > 0) {
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text('Assessment Team:', 10, yPos);
          yPos += 7;
          
          autoTable(doc, {
            startY: yPos,
            head: [['Capacity', 'Name']],
            body: naf6Data.rptrNaf6.map(member => [member.Capacity, member.Name]),
            styles: { fontSize: 8, cellPadding: 2 },
            headStyles: { fillColor: [102, 126, 234], textColor: 255 },
            margin: { left: 10, right: 10 },
          });
          
          yPos = doc.lastAutoTable.finalY + 10;
        }
        
        checkPageBreak(30);
        
        // Assessment Summary
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Assessment Summary:', 10, yPos);
        yPos += 7;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const summaryLines = doc.splitTextToSize(naf6Data.lblNaf6AssmtSmry || '', pageWidth - 20);
        doc.text(summaryLines, 10, yPos);
        yPos += summaryLines.length * 5 + 10;
        
        checkPageBreak(40);
        
        // Recommendation
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text('Recommendation:', 10, yPos);
        yPos += 7;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        const recomLines = doc.splitTextToSize(naf6Data.lblNaf6Recommend || '', pageWidth - 20);
        doc.text(recomLines, 10, yPos);
        yPos += recomLines.length * 5 + 10;
      }

      // 3. ASSESSOR SUMMARY ON NON-CONFORMITY (NAF-4)
      if (reportData.naf4 && reportData.naf4.length > 0) {
        addSectionHeader('NAF-4: Non-Conformities Details');
        
        reportData.naf4.forEach((nc, index) => {
          checkPageBreak(60);
          
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text(`NC ${index + 1}: ${nc.rowNo}`, 10, yPos);
          yPos += 6;
          
          autoTable(doc, {
            startY: yPos,
            body: [
              ['Assessor', nc.Name],
              ['Capacity', nc.Capacity],
              ['Date', nc.Date],
              ['Sub Category', nc.SubCategory],
              ['Standard', nc.NC_Standard],
              ['Clause', nc.NC_Clause],
              ['Observation', nc.AnsRemarks],
              ['Corrective Action Proposed', nc.VerificationRemarks],
              ['Lab Action Taken', nc.CorrectiveAction],
              ['LA Remarks', nc.LA_Remarks],
            ],
            styles: { fontSize: 8, cellPadding: 2 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 } },
            margin: { left: 10, right: 10 },
          });
          
          yPos = doc.lastAutoTable.finalY + 10;
        });
      }

      // 5. TESTING WITNESSED (NAF-3A)
      if (reportData.naf3a && reportData.naf3a.length > 0) {
        addSectionHeader('NAF-3A: Testing/Re-testing Witnessed');
        
        reportData.naf3a.forEach((test, index) => {
          checkPageBreak(70);
          
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text(`Test ${index + 1}`, 10, yPos);
          yPos += 6;
          
          autoTable(doc, {
            startY: yPos,
            body: [
              ['Assessor', test.AssessorName],
              ['Location', test.LabLocationName],
              ['Discipline', test.disciplineName],
              ['Group', test.groupName],
              ['Product/Material', test.GroupTested],
              ['Test Type', test.TypeOfTest],
              ['Standard', test.scopeyear],
              ['Range/Unit', `${test.unit} (${test.measr})`],
              ['Sample ID', test.sampleId],
              ['Earlier Test Date', test.earlierTestdate],
              ['Earlier Tested By', test.earlierTestedBy],
              ['Earlier Result', test.earlierReportedResults],
              ['Witnessed Result', test.resultOfTestWitnessed1],
              ['Same Person', test.resultOfTestWitnessedSamePerson],
              ['Reference Material', test.refernceMaterial],
              ['Specified Value', test.specifiedValue],
              ['RM Test Result', test.resultOfTestWitnessed2],
              ['Deviations', test.deviationObserved],
              ['Observation', test.observation],
              ['Conclusion', test.conclusion],
            ],
            styles: { fontSize: 8, cellPadding: 2 },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
            margin: { left: 10, right: 10 },
          });
          
          yPos = doc.lastAutoTable.finalY + 10;
        });
      }

      // 6. ATTENDANCE SHEET (NAF-1A)
      if (reportData.naf1a && reportData.naf1a.attendanceList) {
        addSectionHeader('NAF-1A: Attendance Sheet');
        
        doc.setFontSize(9);
        doc.text(`Laboratory: ${reportData.naf1a.cabName}`, 10, yPos);
        yPos += 5;
        doc.text(`Opening Meeting: ${reportData.naf1a.openingDate} at ${reportData.naf1a.openingTime}`, 10, yPos);
        yPos += 5;
        doc.text(`Closing Meeting: ${reportData.naf1a.closingDate} at ${reportData.naf1a.closingTime}`, 10, yPos);
        yPos += 10;
        
        checkPageBreak(30);
        
        autoTable(doc, {
          startY: yPos,
          head: [['Meeting', 'Name', 'Designation']],
          body: reportData.naf1a.attendanceList.map(att => [
            att.meeting,
            att.labpersonal,
            att.designation
          ]),
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [102, 126, 234], textColor: 255 },
          margin: { left: 10, right: 10 },
        });
        
        yPos = doc.lastAutoTable.finalY + 10;
      }

      // 7. SCOPE OF ACCREDITATION (Form 72)
      if (reportData.form72 && reportData.form72.length > 0) {
        addSectionHeader('Form 72: Scope of Accreditation');
        
        autoTable(doc, {
          startY: yPos,
          head: [['Assessor', 'Location', 'Discipline', 'Group', 'Test', 'Standard', 'Range', 'Status']],
          body: reportData.form72.map(scope => [
            scope.AssessorName,
            scope.LabLocationName,
            scope.disciplineName,
            scope.groupName,
            scope.TypeOfTest,
            scope.scopeyear,
            `${scope.unit} ± ${scope.measr}`,
            scope.status
          ]),
          styles: { fontSize: 7, cellPadding: 1.5 },
          headStyles: { fillColor: [102, 126, 234], textColor: 255 },
          margin: { left: 10, right: 10 },
        });
        
        yPos = doc.lastAutoTable.finalY + 10;
      }

      // 8. DECLARATION OF IMPARTIALITY (Form 74)
      if (reportData.form74 && reportData.form74.length > 0) {
        addSectionHeader('Form 74: Declaration of Impartiality');
        
        autoTable(doc, {
          startY: yPos,
          head: [['Assessor Name', 'Ex-Employee', 'CAB Relation']],
          body: reportData.form74.map(dec => [
            dec.Name,
            dec.isExEmployee === '1' ? 'Yes' : 'No',
            dec.isCabRelation === '1' ? 'Yes' : 'No'
          ]),
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [102, 126, 234], textColor: 255 },
          margin: { left: 10, right: 10 },
        });
        
        yPos = doc.lastAutoTable.finalY + 10;
      }

      // Footer on last page
      checkPageBreak(20);
      doc.setFillColor(102, 126, 234);
      doc.rect(10, yPos, pageWidth - 20, 10, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('End of Assessment Report', pageWidth / 2, yPos + 7, { align: 'center' });

      // Add page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
      }

      // Save PDF
      doc.save(`Form217_Assessment_Report_${appId}.pdf`);
      showSnackbar('PDF downloaded successfully', 'success');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      showSnackbar('Error generating PDF', 'error');
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleGoBack = () => {
    window.history.back();
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="80vh"
        gap={2}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="primary">Loading Assessment Report...</Typography>
      </Box>
    );
  }

  if (!reportData) {
    return (
      <Container maxWidth="lg">
        <Box 
          p={4} 
          mt={4}
          textAlign="center"
          sx={{
            background: 'linear-gradient(135deg, #fff5f5 0%, #ffe0e0 100%)',
            borderRadius: 2,
            border: '2px solid #f44336'
          }}
        >
          <AssignmentIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            No Report Data Available
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Unable to load assessment report. Please try again later.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ p: 2 }}>
      <style>{`
        @media print {
          .leaflet-control-zoom { display: none !important; }
          .leaflet-tile-pane { opacity: 1 !important; }
          .leaflet-map-pane { transform: none !important; }
          .leaflet-container {
            height: 150px !important;
            width: 100% !important;
            background: #f0f0f0 !important;
          }
          .MuiPaper-root { box-shadow: none !important; border: 1px solid #ccc; }
        }
      `}</style>
      <Fade in={true} timeout={800}>
        <Box>
          {/* Header Section */}
          <Paper
            elevation={3}
            sx={{
              mb: 4,
              p: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              borderRadius: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <AssignmentIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Assessment Report - Form 217
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                  National Accreditation Board for Testing and Calibration
                  Laboratories
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.3)" }} />

            {/* Report Metadata */}
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip
                label={`Lab Type: ${reportData?.naf6?.lblNaf6field || "N/A"}`}
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
              />
            </Box>
          </Paper>

          {/* Action Buttons */}
          {/* <Box sx={{ mb: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              size="medium"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                fontWeight: 600
              }}
            >
              Go Back
            </Button>
            <Button 
              variant="contained" 
              size="medium"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPDF}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                }
              }}
            >
              Download Report
            </Button>
          </Box> */}
          <Box sx={{ mb: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
           <Button 
              variant="contained" 
              size="medium"
              startIcon={<Print/>}
              onClick={() => {
              setTimeout(() => window.print(), 500);
              }}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                }
              }}
            >
              Print
            </Button>
            </Box>

          {/* Report Sections */}
          <Box sx={{ "& > *": { mb: 4 } }}>
            {/* Selfie Section */}
            {reportData.selfies && reportData.selfies.length > 0 && (
              <Fade in={true} timeout={1000}>
                <Box>
                  <SelfieSection data={reportData.selfies} />
                </Box>
              </Fade>
            )}

            {/* Summary of Assessment (NAF-6) */}
            {/* {reportData.naf6 && (
              <Fade in={true} timeout={1200}>
                <Box>
                  <AssessmentSummary data={reportData.naf6} />
                </Box>
              </Fade>
            )} */}

            {/* Summary of Assessment (NAF-6) */}
            {reportData.naf6 &&
              (isDisplayNew01052026 ? (
                <Fade in={true} timeout={1200}>
                  <Box>
                    <AssessmentSummaryNew
                      data={reportData.naf6}
                      appId={appId}
                      assessmentId={assessmentId}
                    />
                  </Box>
                </Fade>
              ) : isDisplayNew15052026 ? (
                <Fade in={true} timeout={1200}>
                  <Box>
                    <AssessmentSummaryNew2
                      data={reportData.naf6}
                      appId={appId}
                      assessmentId={assessmentId}
                    />
                  </Box>
                </Fade>
              ) : (
                <Fade in={true} timeout={1200}>
                  <Box>
                    <AssessmentSummary data={reportData.naf6} />
                  </Box>
                </Fade>
              ))}

            {/* Assessor's Summary on Non-Conformity (NAF-4) */}
            {reportData.naf4 && reportData.naf4.length > 0 && (
              <Fade in={true} timeout={1600}>
                <Box>
                  <AssessorSummaryNC data={reportData.naf4} />
                </Box>
              </Fade>
            )}

            {/* Testing Witnessed (NAF-3A) */}
            {reportData.naf3a && reportData.naf3a.length > 0 && (
              <Fade in={true} timeout={2000}>
                <Box>
                  <TestingWitnessed data={reportData.naf3a} />
                </Box>
              </Fade>
            )}

            {/* Attendance Sheet (NAF-1A) */}
            {reportData.naf1a &&
              reportData.naf1a.attendanceList &&
              reportData.naf1a.attendanceList.length > 0 && (
                <Fade in={true} timeout={2200}>
                  <Box>
                    <AttendanceSheet data={reportData.naf1a} />
                  </Box>
                </Fade>
              )}

            {/* Scope of Accreditation (Form 72) */}
            {reportData.form72 && reportData.form72.length > 0 && (
              <Fade in={true} timeout={2600}>
                <Box>
                  <ScopeAccreditation
                    data={reportData.form72}
                    labType={reportData?.naf6?.lblNaf6field}
                  />
                </Box>
              </Fade>
            )}

            {/* Declaration of Impartiality (Form 74) */}
            {reportData.form74 && reportData.form74.length > 0 && (
              <Fade in={true} timeout={2800}>
                <Box>
                  <DeclarationImpartiality data={reportData.form74} />
                </Box>
              </Fade>
            )}
          </Box>

          {/* End of Report */}
          <Fade in={true} timeout={3000}>
            <Paper
              elevation={4}
              sx={{
                mt: 6,
                mb: 4,
                background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                borderRadius: 2,
                border: "2px solid #667eea",
              }}
            >
              <Box sx={{ p: 4, textAlign: "center" }}>
                <AssignmentIcon
                  sx={{ fontSize: 50, color: "#667eea", mb: 2 }}
                />
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color="primary"
                  gutterBottom
                >
                  End of Assessment Report
                </Typography>
                <Divider sx={{ my: 2, maxWidth: 400, mx: "auto" }} />
                <Typography variant="body2" color="text.secondary">
                  This concludes the Form 217 Assessment Report
                </Typography>
              </Box>
            </Paper>
          </Fade>
        </Box>
      </Fade>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Form217Report;
